// Theme Studio（本番PWA内のビルダー・② 移植）の状態と操作。
// reference の静的ビルダーから「テーマ編集＋Worker公開」を Preact/Bearer 認証下へ移植。
// 編集関数は reference/core/builder.mjs の純関数を再利用（SSOT・複製しない）。
import { signal } from "@preact/signals";
import { listApps, getDraftApp, putDraft, publishApp } from "../api/client";
import {
  setBrandToken,
  setBrandBase,
  setViewType,
  setViewSort,
  setViewGroup,
  addFieldBlock,
  removeBlock,
  moveBlock,
} from "../../../reference/core/builder.mjs";
import { derivePalette } from "../render/model.mjs";
import type { AppDefinition, ViewType } from "../types";

export type BuilderStatus = { kind: "idle" | "loading" | "ok" | "err"; msg: string };

/** 編集中のアプリ（Worker の下書きを開いたもの）。 */
export const builderAppSig = signal<AppDefinition | null>(null);
/** Worker のアプリ一覧（「開く」プルダウン）。 */
export const builderAppsSig = signal<Array<{ id: string; name?: string }>>([]);
/** プレビューのライト/ダーク。 */
export const previewModeSig = signal<"light" | "dark">("light");
/** 編集中のビュー ID（構成編集の対象。既定=home）。 */
export const editViewIdSig = signal<string | null>(null);
/** 直近の操作結果メッセージ。 */
export const builderStatusSig = signal<BuilderStatus>({ kind: "idle", msg: "" });

function setStatus(kind: BuilderStatus["kind"], msg: string) {
  builderStatusSig.value = { kind, msg };
}

/** Worker のアプリ一覧を取得。 */
export async function loadBuilderApps(): Promise<void> {
  setStatus("loading", "アプリ一覧を取得中…");
  try {
    builderAppsSig.value = await listApps();
    setStatus("idle", "");
  } catch (e) {
    setStatus("err", `一覧取得エラー: ${(e as Error).message}`);
  }
}

/** 既存アプリの下書きを Worker から開いて編集開始。 */
export async function openBuilderApp(id: string): Promise<void> {
  setStatus("loading", `${id} を取得中…`);
  try {
    const app = await getDraftApp(id);
    builderAppSig.value = app;
    editViewIdSig.value = homeViewId(app);
    const base = app.brand?.base;
    previewModeSig.value = base === "dark" ? "dark" : "light"; // auto はプレビュー既定=light
    setStatus("ok", `「${id}」を開きました。テーマとビュー構成を編集して公開できます。`);
  } catch (e) {
    setStatus("err", `取得エラー: ${(e as Error).message}`);
  }
}

/** デザイントークンを1つ上書き（純関数 setBrandToken を経由）。 */
export function editBuilderToken(key: string, value: string): void {
  const app = builderAppSig.value;
  if (!app) return;
  builderAppSig.value = setBrandToken(app, key, value) as AppDefinition;
}

/** メインカラー1つから配色（主色＋アクセント）を一括導出して適用（個別に触らなくてよい）。 */
export function applyThemeColor(hex: string): void {
  const app = builderAppSig.value;
  if (!app) return;
  let a: AppDefinition = app;
  for (const [key, value] of Object.entries(derivePalette(hex))) {
    a = setBrandToken(a, key, value) as AppDefinition;
  }
  builderAppSig.value = a;
}

/** ベーステーマ（light/dark/auto）を変更。プレビューも light/dark なら追従。 */
export function setBuilderBase(base: "light" | "dark" | "auto"): void {
  const app = builderAppSig.value;
  if (!app) return;
  builderAppSig.value = setBrandBase(app, base) as AppDefinition;
  if (base === "light" || base === "dark") previewModeSig.value = base;
}

/** プレビューのライト/ダークを反転（編集中の base は変えない）。 */
export function togglePreviewMode(): void {
  previewModeSig.value = previewModeSig.value === "dark" ? "light" : "dark";
}

// ---- ビュー構成の編集（reference/core/builder.mjs の純関数を再利用）--------
/** home ビュー ID の解決（ランタイムと同じ規則）。 */
export function homeViewId(app: AppDefinition): string | null {
  return app.settings?.homeViewId ?? app.navigation?.[0]?.viewId ?? app.views?.[0]?.id ?? null;
}
function updateApp(fn: (app: AppDefinition) => AppDefinition): void {
  const app = builderAppSig.value;
  if (!app) return;
  builderAppSig.value = fn(app);
}
/** 編集対象ビューを切り替え（プレビューもこのビューになる）。 */
export function setEditView(viewId: string): void {
  editViewIdSig.value = viewId;
}
/** ビューのタイプ変更（list/table/gallery/board/…）。 */
export function changeViewType(type: ViewType): void {
  const v = editViewIdSig.value;
  if (v) updateApp((a) => setViewType(a, v, type) as AppDefinition);
}
/** 並び替え（単一列＋昇降。columnId 空で解除）。 */
export function changeViewSort(columnId: string, dir: "asc" | "desc"): void {
  const v = editViewIdSig.value;
  if (v) updateApp((a) => setViewSort(a, v, columnId ? [{ columnId, dir }] : []) as AppDefinition);
}
/** グループ化（columnId 空で解除。agg=count/sum）。 */
export function changeViewGroup(columnId: string, agg: "count" | "sum", valueColumnId?: string): void {
  const v = editViewIdSig.value;
  if (v) updateApp((a) => setViewGroup(a, v, columnId ? { groupByColumnId: columnId, agg, valueColumnId } : {}) as AppDefinition);
}
/** 列を field ブロックとしてビューに追加。 */
export function addViewField(columnId: string): void {
  const v = editViewIdSig.value;
  if (v && columnId) updateApp((a) => addFieldBlock(a, v, columnId) as AppDefinition);
}
/** ブロックを削除。 */
export function removeViewBlock(blockId: string): void {
  const v = editViewIdSig.value;
  if (v) updateApp((a) => removeBlock(a, v, blockId) as AppDefinition);
}
/** ブロックを上下に並べ替え。 */
export function moveViewBlock(blockId: string, dir: "up" | "down"): void {
  const v = editViewIdSig.value;
  if (v) updateApp((a) => moveBlock(a, v, blockId, dir) as AppDefinition);
}

/** 編集中アプリを Worker に保存→検証→公開。 */
export async function publishBuilderApp(): Promise<void> {
  const app = builderAppSig.value;
  if (!app) return;
  setStatus("loading", `${app.id} を公開中…`);
  try {
    await putDraft(app);
    const r = await publishApp(app.id);
    const warn = r.warnings.length ? `（警告 ${r.warnings.length} 件）` : "";
    setStatus("ok", `☁ 公開しました（seq ${r.seq} / v${r.version}）${warn}`);
  } catch (e) {
    setStatus("err", `公開に失敗: ${(e as Error).message}`);
  }
}
